import type { SubQuestion } from 'src/types/helios/Question';

/**
 * Backfill of structural sub-question fields from the canonical
 * (default-language) definition onto a translated copy. Translated packs
 * carry display text only, so each field is copied when the translated
 * copy lacks it (missing or null); a field the translation does carry wins. Conditional
 * spreads keep absent props absent (exactOptionalPropertyTypes).
 *
 * Shared by the mobile perform page (computeSubQuestionsForDisplay) and
 * the desktop getQuestionContent so both use one field list.
 */

/** The four OnPass/OnFail requirement pairs of a scored sub-question. */
export function backfillScoredRequirementFlags(
  sq: SubQuestion,
  canonical: SubQuestion,
): Partial<SubQuestion> {
  return {
    ...(sq.requireCommentOnFail == null && canonical.requireCommentOnFail != null
      ? { requireCommentOnFail: canonical.requireCommentOnFail }
      : {}),
    ...(sq.requireCommentOnPass == null && canonical.requireCommentOnPass != null
      ? { requireCommentOnPass: canonical.requireCommentOnPass }
      : {}),
    ...(sq.requireEvidenceOnFail == null && canonical.requireEvidenceOnFail != null
      ? { requireEvidenceOnFail: canonical.requireEvidenceOnFail }
      : {}),
    ...(sq.requireEvidenceOnPass == null && canonical.requireEvidenceOnPass != null
      ? { requireEvidenceOnPass: canonical.requireEvidenceOnPass }
      : {}),
  };
}

/**
 * The ERT data-collection fields. Translated packs must not lose:
 *   - `requireComment` / `requireAttachment` — the UNCONDITIONAL
 *     data-collection requirements,
 *   - `responseType` — misclassifying a data sub as PASS_FAIL would
 *     flip its whole answer surface and parent derivation,
 *   - `options` — select-typed subs render dead without them
 *     (localized packs MAY carry translated labels, which win),
 *   - `showWhenParentIs` — conditional-visibility flag; translated
 *     packs do not carry it, so in practice it always comes from canonical.
 */
export function backfillErtFields(
  sq: SubQuestion,
  canonical: SubQuestion,
): Partial<SubQuestion> {
  return {
    ...(sq.requireComment == null && canonical.requireComment != null
      ? { requireComment: canonical.requireComment }
      : {}),
    ...(sq.requireAttachment == null && canonical.requireAttachment != null
      ? { requireAttachment: canonical.requireAttachment }
      : {}),
    ...(sq.responseType == null && canonical.responseType != null
      ? { responseType: canonical.responseType }
      : {}),
    ...(sq.options == null && canonical.options != null
      ? { options: canonical.options }
      : {}),
    ...(sq.showWhenParentIs == null && canonical.showWhenParentIs != null
      ? { showWhenParentIs: canonical.showWhenParentIs }
      : {}),
  };
}
